import { useMemo, useState } from 'react';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { Icon } from '@iconify/react';
import { Mood, SecondaryMood } from '@peekapak/types/Mood';
import { getMoodDisplayName } from '@peekapak/types/Mood';
import CustomDropdown from './AdministratorDashboard/AdminReportComponents/CustomDropdown/CustomDropdown';
import { getEmotionColor, EmotionColor } from './ConcerningEmotionsReportUtilities';
import {
  getSecondaryMoodColorFill,
  getSecondaryMoodTextColor,
} from './AdministratorDashboard/MoodboardAdmin/moodboardUtilities';
import { buildWeeklyChartData, buildPeriodPercentages, buildRecentCheckins } from './StudentDetailPanelData';
import { EmotionsReportDisplayRow } from './ConcerningEmotionsReportRows';
import reportStyles from './ConcerningEmotionsReport.module.scss';
import styles from './StudentDetailPanel.module.scss';

interface Props {
  row: EmotionsReportDisplayRow;
  onClose: () => void;
}

interface HoveredSegment {
  mood: Mood | SecondaryMood;
  weekIndex: number;
  x: number;
  y: number;
}

function formatMonthLabel(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const StudentDetailPanel = ({ row, onClose }: Props): JSX.Element => {
  const monthOptions = useMemo(() => {
    const uniqueMonths = new Set(
      row.checkins.map((checkin) => {
        const date = new Date(checkin.createdAt);
        return `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`;
      }),
    );
    const now = new Date();
    uniqueMonths.add(`${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0')}`);

    return [...uniqueMonths]
      .sort()
      .reverse()
      .map((id) => {
        const [year, month] = id.split('-').map(Number);
        return { id, name: formatMonthLabel(year, month) };
      });
  }, [row.checkins]);

  const [selectedMonthId, setSelectedMonthId] = useState(monthOptions[0]?.id ?? '');
  const [hoveredSegment, setHoveredSegment] = useState<HoveredSegment | null>(null);

  const [selectedYear, selectedMonth] = selectedMonthId.split('-').map(Number);

  const weeks = useMemo(
    () => buildWeeklyChartData(row.checkins, selectedYear, selectedMonth, row.gradeBand),
    [row.checkins, selectedYear, selectedMonth, row.gradeBand],
  );

  const chartData = weeks.map((week) => ({ weekLabel: week.weekLabel, ...week.counts }));

  const percentages = useMemo(
    () => buildPeriodPercentages(row.checkins, selectedYear, selectedMonth, row.gradeBand),
    [row.checkins, selectedYear, selectedMonth, row.gradeBand],
  );

  const recentCheckins = useMemo(() => buildRecentCheckins(row.checkins), [row.checkins]);

  // Recharts stacks the first Bar at the bottom; reversed so the stack reads top-down like the moodboard.
  const palette = percentages.map((entry) => entry.feeling).reverse();

  // 6-12 chart and tiles show categories, colored like the School Wide Moodboard's 6-12 chart.
  const chartColor = (feeling: Mood | SecondaryMood): EmotionColor =>
    row.gradeBand !== 'PK-5' && getSecondaryMoodColorFill(feeling)
      ? { backgroundColor: getSecondaryMoodColorFill(feeling), textColor: getSecondaryMoodTextColor(feeling) }
      : getEmotionColor(feeling, row.gradeBand);

  const hoveredDates =
    hoveredSegment && weeks[hoveredSegment.weekIndex]?.dates[hoveredSegment.mood]?.length
      ? weeks[hoveredSegment.weekIndex].dates[hoveredSegment.mood]!.join(', ')
      : null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.panel} onClick={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.studentName}>
              {row.name}
              {row.urgencyTier && <span className={reportStyles[row.urgencyTier]}> {row.urgencyTier}</span>}
            </h2>
            <p className={styles.subtitle}>
              Grade {row.grade} &middot; {row.downCount} low check-ins
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label='Close'>
            <Icon icon='mdi:close' />
          </button>
        </div>

        <div className={styles.section}>
          <p className={styles.sectionLabel}>Check-ins over time (selected period)</p>

          <div className={styles.monthRow}>
            <label className={styles.monthLabel}>Month</label>
            <div className={styles.monthDropdown}>
              <CustomDropdown
                list={monthOptions}
                onSelectSubject={(id) => setSelectedMonthId(id ?? monthOptions[0]?.id ?? '')}
                initialValue={monthOptions[0]?.id ?? ''}
                currentlySelectedItem={selectedMonthId}
                disableChangeLocation={false}
                noFilter
              />
            </div>
          </div>

          <div className={styles.chartRow}>
            <div className={styles.chartColumn}>
              <div className={styles.chartWrapper}>
                <ResponsiveContainer width={340} height={220}>
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <XAxis dataKey='weekLabel' tickLine={false} axisLine={{ stroke: '#ccd1d4' }} fontSize={12} />
                    <YAxis
                      allowDecimals={false}
                      tickLine={false}
                      axisLine={{ stroke: '#ccd1d4' }}
                      fontSize={12}
                      label={{ value: 'Check-ins', angle: -90, position: 'insideLeft', fontSize: 12 }}
                    />
                    {palette.map((mood) => (
                      <Bar
                        key={mood}
                        dataKey={mood}
                        stackId='weeks'
                        fill={chartColor(mood).backgroundColor}
                        onMouseEnter={(_, index, event) =>
                          setHoveredSegment({
                            mood,
                            weekIndex: index,
                            x: (event as unknown as MouseEvent).clientX,
                            y: (event as unknown as MouseEvent).clientY,
                          })
                        }
                        onMouseLeave={() => setHoveredSegment(null)}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                {hoveredDates && (
                  <div className={styles.hoverTooltip} style={{ left: hoveredSegment!.x, top: hoveredSegment!.y }}>
                    {hoveredDates}
                  </div>
                )}
                <p className={styles.caption}>
                  Stacked by emotion, grouped by week. Emotions are in the same order across all bars; no ordering
                  implies better or worse.
                </p>
              </div>
            </div>

            <div className={styles.tiles}>
              <p className={styles.tilesLabel}>This period</p>
              {percentages.map(({ feeling, percentage }) => {
                const color = chartColor(feeling);
                return (
                  <div
                    key={feeling}
                    className={styles.tile}
                    style={{ backgroundColor: color.backgroundColor, color: color.textColor }}
                  >
                    <p className={styles.tilePercentage}>{percentage}%</p>
                    <p className={styles.tileLabel}>{getMoodDisplayName(feeling)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <p className={styles.sectionLabel}>Recent check-ins</p>
          <div className={styles.recentList}>
            {recentCheckins.map((checkin, index) => {
              const color = getEmotionColor(checkin.feeling, row.gradeBand);
              return (
                <div key={index} className={styles.recentItem}>
                  <span
                    className={reportStyles.tag}
                    style={{ backgroundColor: color.backgroundColor, color: color.textColor }}
                  >
                    {getMoodDisplayName(checkin.feeling)}
                  </span>
                  <span className={styles.recentDate}>
                    {new Date(checkin.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailPanel;
